import { useCallback } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation } from '@apollo/client/react'
import { AdminAPI } from '@guestgoodie/graphql'
import { Mail01Icon, UserIcon } from '@hugeicons/core-solid-rounded'

import * as Form from '@/features/form'
import Button from '@/components/ui/Button'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import Input from '@/components/ui/Input'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { useDeleteUser } from '../hooks/useDeleteUser'
import { EditAvatarButton } from './EditAvatarButton'

type FormData = {
  givenName: string
  lastName: string
  email: string
}

export function UserForm() {
  const { t } = useLingui()
  const { user, resetPassword, isResetLoadig } = useCurrentUser()
  const { onDelete } = useDeleteUser()

  const [updateUser] = useMutation(AdminAPI.user.UPDATE, {
    refetchQueries: [AdminAPI.user.ME]
  })

  const update = useCallback(
    async (data: FormData) => {
      await updateUser({
        variables: {
          input: data
        }
      })
    },
    [updateUser]
  )

  return (
    <div className="flex flex-col gap-10 py-4 px-2.5">
      <Form.Root
        onSubmit={update}
        defaultValues={{
          givenName: user.givenName ?? '',
          lastName: user.lastName ?? '',
          email: user.email ?? ''
        }}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row items-stretch w-full gap-4">
            <EditAvatarButton />
            <Form.Field
              name="givenName"
              label={t`Given Name`}
              rules={{
                validate: {
                  required: Form.Validations.requiredWithTrimValidation(
                    t`This field is required`
                  )
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  type="text"
                  Icon={UserIcon}
                  onChange={event => onChange(event.target.value)}
                  placeholder=""
                  autoComplete="given-name webauthn"
                  {...props}
                />
              )}
            </Form.Field>
            <Form.Field
              name="lastName"
              label={t`Family Name`}
              rules={{
                validate: {
                  required: Form.Validations.requiredWithTrimValidation(
                    t`This field is required`
                  )
                }
              }}
            >
              {({ renderProps: { onChange, ...props } }) => (
                <Input
                  type="text"
                  onChange={event => onChange(event.target.value)}
                  placeholder=""
                  autoComplete="family-name webauthn"
                  {...props}
                />
              )}
            </Form.Field>
          </div>
          <Form.Field
            name="email"
            label={t`Email`}
            disabled
            rules={{
              validate: {
                emailValidation: Form.Validations.emailValidation(
                  t`Not a valid email.`
                ),
                required: Form.Validations.requiredWithTrimValidation(
                  t`This field is required`
                )
              }
            }}
          >
            {({ renderProps: { onChange, ...props } }) => (
              <Input
                type="text"
                Icon={Mail01Icon}
                onChange={event => onChange(event.target.value)}
                placeholder=""
                autoComplete="email webauthn work"
                {...props}
                readOnly
              />
            )}
          </Form.Field>
          <div className="flex flex-row justify-end">
            <Form.Submit>
              <Trans>Save</Trans>
            </Form.Submit>
          </div>
        </div>
      </Form.Root>
      <div className="relative border border-red-300 rounded py-7 px-5">
        <div className="flex flex-row items-center justify-center gap-2 absolute top-0 left-0 w-full -translate-y-1/2">
          <span className="text-text-light bg-white px-2">
            <Trans>Danger Zone</Trans>
          </span>
        </div>
        <div className="flex flex-row justify-between gap-2">
          {!user.isOauth && (
            <Button
              type="button"
              loading={isResetLoadig}
              onClick={resetPassword}
            >
              <Trans>Reset password</Trans>
            </Button>
          )}
          <ConfirmDialog
            title={t`Delete Account`}
            message={t`Are your sure that you want to delete your Account? You can reverse it for 30 days by writing our support.`}
            onConfirm={onDelete}
            asChild
          >
            <Button type="button" className="bg-red-500 border-red-500">
              <Trans>Delete Account</Trans>
            </Button>
          </ConfirmDialog>
        </div>
      </div>
    </div>
  )
}
